import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loginUser, registerUser, logoutUser, getCurrentUser } from '../api/authApi';
import { tokenManager } from '../api/apiClient';
import { apiRequest } from '../api/apiClient';
import { BACKEND_URL } from '../config/api';
import { normaliseApiErrorDetail } from '../utils/errorNormaliser';

const AuthContext = createContext(null);

const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/suspended',
  '/system-status',
];

const AUTH_BOOT_RETRY_MS = 1500;

const bootRefreshClient = axios.create({
  baseURL: BACKEND_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

function isPublicAuthPath(pathname) {
  return PUBLIC_AUTH_PATHS.some((path) =>
    pathname === path || pathname.startsWith(`${path}/`)
  );
}

function isTerminalRefreshFailure(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  const userRef = useRef(null);
  const authRetryTimerRef = useRef(null);
  const checkAuthRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Multi-role support
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const activeRole = user?.active_role || user?.role || null;
  const roleSubscriptions = user?.role_subscriptions || {};

  const clearAuthRetryTimer = useCallback(() => {
    if (authRetryTimerRef.current) {
      clearTimeout(authRetryTimerRef.current);
      authRetryTimerRef.current = null;
    }
  }, []);

  const scheduleBootAuthRetry = useCallback(() => {
    if (authRetryTimerRef.current) return;

    authRetryTimerRef.current = window.setTimeout(() => {
      authRetryTimerRef.current = null;
      setLoading(true);
      checkAuthRef.current?.();
    }, AUTH_BOOT_RETRY_MS);
  }, []);

  const checkAuth = useCallback(async () => {
    let keepLoadingForRetry = false;

    try {
      const userData = await getCurrentUser();
      clearAuthRetryTimer();
      setUser(userData);
      userRef.current = userData;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401) {
        // The access token may simply have expired. Use a dedicated refresh
        // client with no apiClient interceptors so a temporary refresh failure
        // cannot recursively dispatch auth:unauthorized.
        let newToken = null;
        let refreshError = null;

        try {
          const refreshRes = await bootRefreshClient.post('/api/auth/refresh', {});
          newToken = refreshRes?.data?.access_token || refreshRes?.data?.token || null;
        } catch (err) {
          refreshError = err;
        }

        if (newToken) {
          tokenManager.set(newToken);

          try {
            const userDataAfterRefresh = await getCurrentUser();
            clearAuthRetryTimer();
            setUser(userDataAfterRefresh);
            userRef.current = userDataAfterRefresh;
            return;
          } catch (retryError) {
            const retryStatus = retryError?.response?.status;
            if (retryStatus !== 401) {
              refreshError = retryError;
            } else {
              refreshError = refreshError || retryError;
            }
          }
        }

        if (refreshError && !isTerminalRefreshFailure(refreshError)) {
          // A network error / 429 / 5xx is not proof the session expired.
          // If a user is already hydrated, leave them in place. On first boot,
          // keep the auth-loading gate up and retry rather than bouncing a
          // valid user to /login because the origin blinked for a moment.
          // eslint-disable-next-line no-console
          console.warn('[auth] boot refresh transient error — keeping session', {
            status: refreshError?.response?.status || null,
            code: refreshError?.code || null,
          });

          if (!userRef.current) {
            keepLoadingForRetry = true;
            scheduleBootAuthRetry();
          }
          return;
        }

        // Genuine terminal refresh failure: the refresh session is no longer
        // valid, so this is the correct time to clear auth.
        clearAuthRetryTimer();
        setUser(null);
        userRef.current = null;
        tokenManager.clear();
      } else {
        // /auth/me itself hit a temporary network/rate-limit/origin error.
        // Never clear an already hydrated user. On first boot, keep the loading
        // gate active and retry so ProtectedRoute cannot falsely redirect.
        // eslint-disable-next-line no-console
        console.warn('[auth] checkAuth transient error — keeping session', status, error?.message);

        if (!userRef.current) {
          keepLoadingForRetry = true;
          scheduleBootAuthRetry();
        }
      }
    } finally {
      if (!keepLoadingForRetry) {
        setLoading(false);
      }
    }
  }, [clearAuthRetryTimer, scheduleBootAuthRetry]);

  useEffect(() => {
    checkAuthRef.current = checkAuth;
  }, [checkAuth]);

  useEffect(() => {
    checkAuth();
    return () => clearAuthRetryTimer();
  }, [checkAuth, clearAuthRetryTimer]);

  // Listen for unauthorized events from API client.
  useEffect(() => {
    const handleUnauthorized = (event) => {
      const reason = event?.detail?.reason;
      const refreshStatus = event?.detail?.refresh_status;

      // Defence in depth for older/mixed bundles: a transient refresh failure
      // must never be treated as a confirmed logout condition.
      if (
        reason === 'refresh_failed'
        && refreshStatus !== 401
        && refreshStatus !== 403
      ) {
        // eslint-disable-next-line no-console
        console.warn('[auth] ignored transient unauthorized event', {
          reason,
          refresh_status: refreshStatus || null,
        });
        return;
      }

      clearAuthRetryTimer();
      setUser(null);
      userRef.current = null;

      const pathname = window.location.pathname;

      if (isPublicAuthPath(pathname)) {
        return;
      }

      navigate('/login', {
        replace: true,
        state: { from: pathname },
      });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, clearAuthRetryTimer]);

  const login = async (email, password) => {
    const data = await loginUser(email, password);
    clearAuthRetryTimer();
    setUser(data);
    userRef.current = data;
    return data;
  };

  const register = async (
    email,
    password,
    name,
    role,
    phone = null,
    referralCode = null,
    dateOfBirth = null,
    city = null,
    phoneCountry = 'GB'
  ) => {
    const data = await registerUser(
      email,
      password,
      name,
      role,
      phone,
      referralCode,
      dateOfBirth,
      city,
      phoneCountry
    );
    clearAuthRetryTimer();
    setUser(data);
    userRef.current = data;
    return data;
  };

  const logout = async () => {
    clearAuthRetryTimer();
    setLoading(true);
    setIsLoggingOut(true);

    try {
      await logoutUser();
    } finally {
      setUser(null);
      userRef.current = null;

      setTimeout(() => {
        setLoading(false);
        setIsLoggingOut(false);
      }, 80);
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const switchRole = async (role) => {
    try {
      const { data } = await apiRequest.post('/api/users/me/roles/switch', { role });

      const switchedToken = data?.access_token || data?.token;
      if (switchedToken) {
        // Keep WebSocket query-token authentication aligned with the fresh
        // httpOnly role cookie issued by the backend.
        tokenManager.set(switchedToken);
      }

      await refreshUser();
      window.dispatchEvent(new CustomEvent('subscription:updated'));
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to switch role';

      throw new Error(message);
    }
  };

  const addRole = async (role) => {
    try {
      const { data } = await apiRequest.post('/api/users/me/roles/add', { role });
      await refreshUser();
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add role';

      throw new Error(message);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isLoggingOut,
        login,
        register,
        logout,
        refreshUser,
        userRoles,
        activeRole,
        roleSubscriptions,
        switchRole,
        addRole,
        token: tokenManager.get(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export function useRequireAuth() {
  const { user, loading } = useAuth();

  return {
    user,
    loading,
    isAuthenticated: !!user,
  };
}

export function formatApiErrorDetail(detail) {
  return normaliseApiErrorDetail(detail);
}
